import React, { useRef, useState } from 'react';
import { SafeSEO } from '@/components/seo/SafeSEO';
import Navigation from '../components/Navigation';
import { SEO_ROUTES } from '@/seo/seo-routes';
import Footer from '../components/Footer';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { UploadCloud, ArrowRight, Plus, X, FileImage } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import ReCAPTCHA from 'react-google-recaptcha';
import { submitSupportTicket, ApiError, uploadAttachments } from '@/api/api';

const MAX_ATTACHMENTS = 5;
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const supportSchema = z.object({
  fullName: z
    .string()
    .min(2, 'Please enter your full name.'),
  email: z
    .string()
    .min(1, 'Email is required.')
    .email('Enter a valid email address.'),
  category: z
    .string()
    .min(1, 'Select an issue category.'),
  priority: z
    .string()
    .min(1, 'Select a priority level.'),
  subject: z
    .string()
    .min(6, 'Subject should be at least 6 characters.')
    .max(120, 'Subject should be under 120 characters.'),
  description: z
    .string()
    .min(30, 'Please provide a bit more detail so we can help quickly.')
    .max(2000, 'Description must be under 2000 characters.'),
  attachments: z.array(z.instanceof(File)).optional(),
  includeSystemInfo: z.boolean().default(true),
});

type SupportTicketFormValues = z.infer<typeof supportSchema>;

const categoryOptions = [
  { value: 'account-access', label: "I can't log in or access my account" },
  { value: 'safety-question', label: 'Product question about Joey Family safety features' },
  { value: 'billing', label: 'Billing or subscription question' },
  { value: 'bug-report', label: 'Something is not working as expected' },
  { value: 'sales', label: 'Talk to sales about Joey Family' },
  { value: 'other', label: 'Something else' },
];

const priorityOptions = [
  { value: 'low', label: 'Low - FYI or general question' },
  { value: 'normal', label: 'Normal - needs help soon' },
  { value: 'high', label: 'High - blocking my family' },
  { value: 'urgent', label: 'Urgent - safety concern' },
];

const ContactUs: React.FC = () => {
  const { toast } = useToast();
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const form = useForm<SupportTicketFormValues>({
    resolver: zodResolver(supportSchema),
    defaultValues: {
      fullName: '',
      email: '',
      category: '',
      priority: '',
      subject: '',
      description: '',
      attachments: [],
      includeSystemInfo: true,
    },
  });

  const onSubmit = async (values: SupportTicketFormValues) => {
  if (!recaptchaToken) {
    toast({
      title: "Please complete the reCAPTCHA",
      description: "Verify that you're not a robot.",
      variant: "destructive",
    });
    return;
  }

  try {
    const files = (values.attachments ?? []) as File[];

    // client-side checks (optional but recommended)
    if (files.length > MAX_ATTACHMENTS) {
      form.setError("attachments", { type: "manual", message: `Please upload up to ${MAX_ATTACHMENTS} files.` });
      return;
    }
    if (files.some((f) => f.size > MAX_FILE_SIZE_BYTES)) {
      form.setError("attachments", { type: "manual", message: `Each file must be smaller than ${MAX_FILE_SIZE_MB}MB.` });
      return;
    }

    const ticketData = {
      fullName: values.fullName,
      email: values.email,
      category: values.category,
      priority: values.priority,
      subject: values.subject,
      description: values.description,
      includeSystemInfo: values.includeSystemInfo,
    };

    // 1) Create ticket + get presigned URLs in SAME call
    const { ticketId, createdAt, uploads } = await submitSupportTicket(ticketData, recaptchaToken, files);

    // 2) Upload to S3 (direct PUT)
    if (files.length > 0) {
      if (!uploads || uploads.length !== files.length) {
        throw new Error("Upload preparation failed (uploads mismatch).");
      }

      await Promise.all(
        uploads.map(async (u, idx) => {
          const file = files[idx];
          const up = await fetch(u.putUrl, {
            method: "PUT",
            headers: { "Content-Type": file.type },
            body: file,
          });
          if (!up.ok) throw new Error(`Upload failed: ${file.name}`);
        })
      );

      // 3) Commit attachment keys to DynamoDB
      await uploadAttachments(
        ticketId,
        createdAt,
        uploads.map((u) => ({ key: u.key, name: u.name, type: u.type, size: u.size }))
      );
    }

    toast({
      title: "Support request sent",
      description: "Thanks! Our support team will reply within one business day.",
    });

    form.reset({
      fullName: "",
      email: "",
      category: "",
      priority: "",
      subject: "",
      description: "",
      attachments: [],
      includeSystemInfo: true,
    });

    setRecaptchaToken(null);
    recaptchaRef.current?.reset();
  } catch (error) {
    toast({
      title: "Something went wrong",
      description: error instanceof Error ? error.message : "We could not send your request. Please try again.",
      variant: "destructive",
    });
    console.error("Failed to submit support ticket", error);
  }
};

  const handleFileChange = (
    files: FileList | null,
    onChange: (value: File[]) => void,
  ) => {
    const nextFiles = Array.from(files ?? []);
    onChange(nextFiles);
  };

  return (
    <>
      <SafeSEO
        title="Contact Joey Support"
        description="Get help with setup, billing and safety questions. Our team can guide you on alerts for bullying, risky contacts and money asks."
        url={SEO_ROUTES.contact}
        image="/img/og/joey-default-1200x630.png"
      />
      <div className="min-h-screen bg-background">
        <Navigation />

        <main className="pt-44 md:pt-56 pb-24 px-6 max-md:px-4">
          <section className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="text-hero font-medium text-joey-cyan mb-6 max-md:text-title">
                Submit a Support Ticket
              </h1>
              <p className="text-subtitle text-muted-foreground max-md:text-body">
                Locked out of your account or have questions about Joey Family? Tell us what&apos;s happening and our support team will respond within one business day.
              </p>
            </div>

            <Card className="border-none shadow-xl">
              <CardHeader className="space-y-2 border-b border-gray-100">
                <CardTitle className="text-2xl text-brand-navy flex items-center gap-2">
                  <UploadCloud className="h-6 w-6 text-joey-cyan" aria-hidden="true" />
                  Send us the details
                </CardTitle>
                <CardDescription className="text-base text-slate-600">
                  The more information you share, the faster we can help. Attach screenshots if you have them.
                </CardDescription>
              </CardHeader>

              <CardContent className="pt-8">
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                    <div className="grid gap-6 md:grid-cols-2">
                      <FormField
                        control={form.control}
                        name="fullName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Full Name</FormLabel>
                            <FormControl>
                              <Input placeholder="Your full name" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email Address</FormLabel>
                            <FormControl>
                              <Input type="email" placeholder="you@example.com" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="grid gap-6 md:grid-cols-2">
                      <FormField
                        control={form.control}
                        name="category"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Issue Category</FormLabel>
                            <FormControl>
                              <select
                                className={cn(
                                  'flex h-11 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-joey-cyan focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50'
                                )}
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                              >
                                <option value="" disabled>
                                  Select a category
                                </option>
                                {categoryOptions.map((option) => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="priority"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Priority</FormLabel>
                            <FormControl>
                              <select
                                className={cn(
                                  'flex h-11 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-joey-cyan focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50'
                                )}
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                              >
                                <option value="" disabled>
                                  Select a priority
                                </option>
                                {priorityOptions.map((option) => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="subject"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Subject</FormLabel>
                          <FormControl>
                            <Input placeholder="Brief description of the issue" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Detailed Description</FormLabel>
                          <FormControl>
                            <Textarea
                              rows={6}
                              placeholder="Please include steps to reproduce the issue, the Joey feature you were using, and any recent changes to your device."
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="attachments"
                      render={({ field }) => {
                        const currentFiles = (field.value ?? []) as File[];
                        const canAddMore = currentFiles.length < MAX_ATTACHMENTS;

                        const handleFileAdd = (event: React.ChangeEvent<HTMLInputElement>) => {
                          const newFiles = Array.from(event.target.files ?? []);
                          const totalFiles = currentFiles.length + newFiles.length;
                          
                          if (totalFiles > MAX_ATTACHMENTS) {
                            toast({
                              title: "Too many files",
                              description: `You can only upload up to ${MAX_ATTACHMENTS} files.`,
                              variant: "destructive",
                            });
                            event.target.value = "";
                            return;
                          }

                          // Check file sizes
                          const oversizedFiles = newFiles.filter(f => f.size > MAX_FILE_SIZE_BYTES);
                          if (oversizedFiles.length > 0) {
                            toast({
                              title: "File too large",
                              description: `Some files exceed ${MAX_FILE_SIZE_MB}MB. Please select smaller files.`,
                              variant: "destructive",
                            });
                            event.target.value = "";
                            return;
                          }

                          field.onChange([...currentFiles, ...newFiles]);
                          event.target.value = "";
                        };

                        const handleFileRemove = (indexToRemove: number) => {
                          const updatedFiles = currentFiles.filter((_, index) => index !== indexToRemove);
                          field.onChange(updatedFiles);
                        };

                        return (
                          <FormItem>
                            <div className="flex items-center justify-between mb-2">
                              <FormLabel>Screenshots (optional)</FormLabel>
                              <FormDescription className="text-xs text-muted-foreground m-0">
                                Up to {MAX_ATTACHMENTS} files • {MAX_FILE_SIZE_MB}MB each
                              </FormDescription>
                            </div>
                            <FormControl>
                              <div className="space-y-3">
                                {/* Display existing files */}
                                {currentFiles.length > 0 && (
                                  <div className="grid gap-3">
                                    {currentFiles.map((file: File, index: number) => (
                                      <div
                                        key={`${file.name}-${file.lastModified}-${index}`}
                                        className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 transition-colors"
                                      >
                                        <FileImage className="h-5 w-5 text-joey-cyan flex-shrink-0" />
                                        <div className="flex-1 min-w-0">
                                          <p className="text-sm font-medium text-slate-900 truncate">
                                            {file.name}
                                          </p>
                                          <p className="text-xs text-muted-foreground">
                                            {(file.size / 1024 / 1024).toFixed(2)} MB
                                          </p>
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => handleFileRemove(index)}
                                          className="flex-shrink-0 p-1 rounded-md hover:bg-slate-200 text-slate-500 hover:text-slate-700 transition-colors"
                                          aria-label={`Remove ${file.name}`}
                                        >
                                          <X className="h-4 w-4" />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {/* Drop zone (only show when no files) */}
                                {currentFiles.length === 0 && (
                                  <label
                                    className={cn(
                                      'flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-joey-cyan/50 bg-joey-cyan/5 px-6 py-8 text-center transition hover:border-joey-cyan hover:bg-joey-cyan/10 cursor-pointer'
                                    )}
                                  >
                                    <UploadCloud className="h-8 w-8 text-joey-cyan" aria-hidden="true" />
                                    <span className="text-sm font-medium text-joey-cyan">
                                      Drop screenshots here, or click to browse
                                    </span>
                                    <span className="text-xs text-muted-foreground">
                                      We&rsquo;ll automatically include filenames with your ticket.
                                    </span>
                                    <input
                                      type="file"
                                      className="hidden"
                                      multiple
                                      accept=".png,.jpg,.jpeg,.gif"
                                      onChange={handleFileAdd}
                                      onBlur={field.onBlur}
                                    />
                                  </label>
                                )}

                                {/* Add more button (only show when files exist and can add more) */}
                                {canAddMore && currentFiles.length > 0 && (
                                  <label
                                    className={cn(
                                      'flex items-center justify-center gap-2 rounded-lg border border-dashed border-joey-cyan/50 bg-joey-cyan/5 px-4 py-3 text-center transition hover:border-joey-cyan hover:bg-joey-cyan/10 cursor-pointer'
                                    )}
                                  >
                                    <Plus className="h-5 w-5 text-joey-cyan" aria-hidden="true" />
                                    <span className="text-sm font-medium text-joey-cyan">
                                      Add more ({currentFiles.length}/{MAX_ATTACHMENTS})
                                    </span>
                                    <input
                                      type="file"
                                      className="hidden"
                                      multiple
                                      accept=".png,.jpg,.jpeg,.gif"
                                      onChange={handleFileAdd}
                                      onBlur={field.onBlur}
                                    />
                                  </label>
                                )}
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        );
                      }}
                    />

                    <FormField
                      control={form.control}
                      name="includeSystemInfo"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-xl border border-slate-200 bg-slate-50 p-4">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={(checked) => field.onChange(!!checked)}
                            />
                          </FormControl>
                          <div className="space-y-1 leading-snug">
                            <FormLabel className="text-sm font-medium">
                              Include basic system information with my ticket
                            </FormLabel>
                            <FormDescription className="text-xs text-muted-foreground">
                              Helpful for debugging account access issues. No personal content is included.
                            </FormDescription>
                          </div>
                        </FormItem>
                      )}
                    />

                    <div className="flex justify-center">
                      <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey={RECAPTCHA_SITE_KEY}
                        onChange={setRecaptchaToken}
                        theme="light"
                        size="normal"
                      />
                    </div>

                    <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                      <p className="text-sm text-muted-foreground">
                        Joey Family is owned by Laneway Analytics Pty Ltd. Support hours: Monday-Friday, 9am-5pm AEST.
                      </p>
                      <Button
                        type="submit"
                        className="px-8 py-6 text-base font-semibold"
                        disabled={form.formState.isSubmitting || !recaptchaToken}
                      >
                        {form.formState.isSubmitting ? (
                          'Sending...'
                        ) : (
                          <>
                            Submit Support Ticket
                            <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
                          </>
                        )}
                      </Button>
                    </div>
                  </form>
                </Form>
              </CardContent>
            </Card>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default ContactUs;

